import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { DoorClosed, Footprints, MessageCircle, Ruler } from 'lucide-react'
import Reveal from '@/components/generals/reveal'

const BEHAVIOURS = [
    {
        icon: Ruler,
        title: "Keeps a comfortable distance",
        body: "About an arm and a half away, closer only when you ask."
    },
    {
        icon: Footprints,
        title: "Slows down around children and pets",
        body: "And steps around a sleeping dog rather than over it."
    },
    {
        icon: MessageCircle,
        title: "Says what it is about to do",
        body: "A soft word before it reaches, lifts or opens anything."
    },
    {
        icon: DoorClosed,
        title: "Pauses at closed doors",
        body: "It asks before it goes into a room that has been shut."
    }
]

const WithPeople: FC = (): ReactNode => {
    return (
        <section id='with-people' aria-labelledby='people-title' className='bg-background'>
            <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-28'>
                <div className='grid gap-6 lg:grid-cols-12 lg:gap-10'>
                    <Reveal className='lg:col-span-7'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember-deep'>
                            Around people
                        </p>

                        <h2 id='people-title' className='mt-5 font-heading text-[clamp(2.6rem,5.4vw,5rem)] leading-[0.96] tracking-[-0.02em]'>
                            Calm company, <em className='italic text-ember-deep'>by design.</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={150} className='flex items-end lg:col-span-5'>
                        <p className='max-w-md text-lg leading-relaxed text-muted-foreground'>
                            Oriel moves at a human pace, speaks softly and never hovers. The aim is simple: after a week, you stop noticing it's there.
                        </p>
                    </Reveal>
                </div>

                <div className='mt-12 grid gap-3 md:mt-16 lg:grid-cols-12 lg:grid-rows-[auto_auto]'>
                    <Reveal className='lg:col-span-7 lg:row-span-2'>
                        <figure className='group relative h-[28rem] overflow-hidden rounded-[2rem] md:h-[36rem] lg:h-full lg:min-h-[44rem]'>
                            <Image
                                src='/images/people-tea.jpg'
                                alt="Oriel leaning forward to hand a cup of tea on a saucer to a smiling older woman reading in a linen armchair."
                                fill
                                sizes='(min-width: 1024px) 58vw, 100vw'
                                className='scroll-drift object-cover object-[40%_50%]'
                            />

                            <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent' />

                            <figcaption className='absolute inset-x-0 bottom-0 flex flex-col gap-2 p-6 text-ink-foreground md:p-8'>
                                <span className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                                    Handing over
                                </span>

                                <span className='max-w-md font-heading text-3xl leading-tight md:text-4xl'>
                                    Offers things at your height, and waits until you have them.
                                </span>
                            </figcaption>
                        </figure>
                    </Reveal>

                    <Reveal delay={120} className='lg:col-span-5'>
                        <figure className='relative h-[22rem] overflow-hidden rounded-[2rem] md:h-[26rem]'>
                            <Image
                                src='/images/people-child.jpg'
                                alt="Oriel kneeling to a young girl's eye level as she proudly shows it her crayon drawing of a house."
                                fill
                                sizes='(min-width: 1024px) 40vw, 100vw'
                                className='scroll-drift object-cover object-[35%_35%]'
                            />

                            <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-ink/75 via-transparent to-transparent' />

                            <figcaption className='absolute inset-x-0 bottom-0 p-6 text-ink-foreground'>
                                <span className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                                    Eye level
                                </span>

                                <span className='mt-2 block max-w-sm font-heading text-2xl leading-tight md:text-3xl'>
                                    Kneels to listen to the smallest people in the house.
                                </span>
                            </figcaption>
                        </figure>
                    </Reveal>

                    <Reveal delay={200} className='lg:col-span-5'>
                        <ul className='grid h-full gap-px overflow-hidden rounded-[2rem] border border-border bg-border sm:grid-cols-2'>
                            {BEHAVIOURS.map(item => (
                                <li key={item.title} className='flex flex-col gap-3 bg-card p-6'>
                                    <item.icon className='size-5 text-ember-deep' aria-hidden='true' />

                                    <h3 className='text-[15px] font-semibold leading-snug text-foreground'>
                                        {item.title}
                                    </h3>

                                    <p className='text-sm leading-relaxed text-muted-foreground'>
                                        {item.body}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </Reveal>

                    <Reveal delay={120} className='lg:col-span-12'>
                        <figure className='grid items-center gap-6 rounded-[2rem] bg-sand p-7 md:grid-cols-12 md:p-10'>
                            <blockquote className='md:col-span-9'>
                                <p className='font-heading text-[clamp(1.75rem,3vw,2.6rem)] leading-[1.15] text-foreground'>
                                    &ldquo;The first week we watched it constantly. By the third, we only noticed it when the kitchen was already clean.&rdquo;
                                </p>
                            </blockquote>

                            <figcaption className='flex items-center gap-4 md:col-span-3 md:justify-end'>
                                <span className='h-px w-10 bg-foreground/30 md:hidden' />

                                <span className='text-sm leading-relaxed text-muted-foreground'>
                                    <span className='block font-medium text-foreground'>
                                        The Alvarez household
                                    </span>
                                    Pilot home, Portland
                                </span>
                            </figcaption>
                        </figure>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}

export default WithPeople
